/**
 * ECSR-624 — normalize the Okendo star-rating summary inside product cards.
 *
 * Product cards render `snippets/okendo-reviews-product-rating-summary.liquid`, which
 * inlines Okendo's pre-rendered `product.metafields.okendo.StarRatingSnippet` HTML and is
 * then replaced client-side by the Okendo widget script. Either source can carry wording
 * ("4.3 Stars", "3 Reviews") that the merchant's current Okendo display setting no longer
 * asks for — the metafield snapshot in particular is only regenerated when Okendo re-syncs
 * that product, so a settings change is not reflected on already-cached snippets.
 *
 * base.css handles the common markup shapes declaratively (hides the rating value and the
 * "Stars"/"Reviews" word spans, wraps the count in parentheses). This script covers the
 * cases CSS cannot: wording that sits in the same text node as the count number. It
 * reduces the visible count text to the bare number and moves the original text into a
 * visually hidden span so screen readers keep the full label. Parentheses are always added
 * by CSS, never here, so they can never be doubled up.
 *
 * Scoped to `.price-reviews [data-oke-star-rating]` (card-level widgets only) and re-runs
 * when Okendo, Nosto, or the facet/pagination re-render swaps card markup in.
 */
(function () {
  'use strict';

  var WIDGET_SELECTOR = '.price-reviews [data-oke-star-rating]';
  // Elements known to hold only the review count. A number found in one of these can be
  // trusted to BE the count.
  var COUNT_SELECTORS = ['.oke-sr-count', '[class*="oke-sr-count"]'];
  // Generic label elements that may hold the whole "4.3 Stars (3 Reviews)" string. A number
  // here is only trusted when the surrounding text proves which number is the count.
  var LABEL_SELECTORS = ['.oke-sr-label--text', '.oke-sr-label'];
  var NUMBER_PATTERN = /\d[\d.,]*/;
  var PARENTHESISED_NUMBER_PATTERN = /\((\d[\d.,]*)/;
  var LABELLED_COUNT_PATTERN = /(\d[\d.,]*)\s*(?:reviews?|ratings?)/i;
  var LETTER_PATTERN = /[a-z]/i;

  function firstMatch(selectors, widget) {
    for (var i = 0; i < selectors.length; i++) {
      var candidate = widget.querySelector(selectors[i]);
      if (candidate && NUMBER_PATTERN.test(candidate.textContent || '')) return candidate;
    }
    return null;
  }

  function extractCount(text, isDedicatedCountElement) {
    var match = text.match(PARENTHESISED_NUMBER_PATTERN) || text.match(LABELLED_COUNT_PATTERN);
    if (match) return match[1];
    // A shared label such as "4.3 Stars 3 Reviews" without either marker is ambiguous —
    // leave it to CSS rather than risk showing the average rating as the count.
    if (!isDedicatedCountElement) return null;
    match = text.match(NUMBER_PATTERN);
    return match ? match[0] : null;
  }

  function normalizeWidget(widget) {
    var count = firstMatch(COUNT_SELECTORS, widget);
    var isDedicatedCountElement = !!count;
    if (!count) count = firstMatch(LABEL_SELECTORS, widget);
    if (!count || count.getAttribute('data-ecsr624-count') === '1') return;

    var text = (count.textContent || '').trim();
    if (!LETTER_PATTERN.test(text)) {
      // Already number-only; CSS supplies the parentheses.
      count.setAttribute('data-ecsr624-count', '1');
      return;
    }

    var number = extractCount(text, isDedicatedCountElement);
    if (!number) return;

    // Mark before mutating: guarantees we never reprocess this node, even though the hidden
    // span we are about to add still reports letters via textContent.
    count.setAttribute('data-ecsr624-count', '1');

    var visible = document.createElement('span');
    visible.className = 'oke-sr-count-number';
    visible.textContent = number;

    var hidden = document.createElement('span');
    hidden.setAttribute('data-ecsr624-hidden-text', '1');
    hidden.textContent = ' ' + text;

    count.textContent = '';
    count.appendChild(visible);
    count.appendChild(hidden);
  }

  function normalizeAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var widgets = scope.querySelectorAll(WIDGET_SELECTOR);
    for (var i = 0; i < widgets.length; i++) normalizeWidget(widgets[i]);
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.setTimeout(function () {
      scheduled = false;
      normalizeAll(document);
    }, 100);
  }

  function start() {
    normalizeAll(document);

    if (!('MutationObserver' in window)) return;
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
          schedule();
          return;
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
